import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import {
  LiksMembersPanel,
  type LiksMembersPanelLabels,
  type LiksMembersPanelMember,
} from '../../components/liks/LiksMembersPanel/LiksMembersPanel';
import {
  canLeaveChatbot,
  canManageChatbotMembers,
  canManageChatbotMemberTarget,
} from '../../features/chatbots/chatbotPermissions';
import {
  useInviteChatbotMemberMutation,
  useRemoveChatbotMemberMutation,
  useUpdateChatbotMemberRoleMutation,
} from '../../features/chatbots/mutations/useChatbotMemberMutations';
import { useTenantUsersQuery } from '../../features/tenant/queries/useTenantUsersQuery';
import { formatDirectoryUserDisplayName } from '../../features/user/userDisplay';
import { useI18n } from '../../i18n/useI18n';
import { toastMutationError } from '../../query/mutationToasts';
import { toast } from '../../toast/toast';
import type { ChatbotMemberRole, ChatbotMembership } from '../../types/chatbot';

const MEMBER_ROLES: ChatbotMemberRole[] = ['ADMIN', 'EDITOR', 'VIEWER'];

type ChatbotMembersPanelProps = {
  chatbotId: string;
  chatbotName: string;
  members: ChatbotMembership[];
};

function toPanelMember(
  member: ChatbotMembership,
): LiksMembersPanelMember<ChatbotMemberRole> | null {
  if (
    typeof member.id !== 'string' ||
    member.id.length === 0 ||
    typeof member.userId !== 'string' ||
    member.userId.length === 0 ||
    !member.role
  ) {
    return null;
  }

  return {
    id: member.id,
    userId: member.userId,
    role: member.role,
  };
}

export function ChatbotMembersPanel({
  chatbotId,
  chatbotName,
  members,
}: ChatbotMembersPanelProps) {
  const { translate } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const inviteMemberMutation = useInviteChatbotMemberMutation();
  const updateMemberRoleMutation = useUpdateChatbotMemberRoleMutation();
  const removeMemberMutation = useRemoveChatbotMemberMutation();
  const directoryUsersQuery = useTenantUsersQuery({ enabled: true });

  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [isLeaving, setIsLeaving] = useState(false);

  const panelMembers = useMemo(
    () =>
      members
        .map(toPanelMember)
        .filter(
          (member): member is LiksMembersPanelMember<ChatbotMemberRole> =>
            member !== null,
        ),
    [members],
  );

  const membersByUserId = useMemo(() => {
    const map = new Map<string, ChatbotMembership>();
    for (const member of members) {
      if (member.userId) {
        map.set(member.userId, member);
      }
    }
    return map;
  }, [members]);

  const currentMember = user?.id ? membersByUserId.get(user.id) : undefined;
  const currentMemberRole = currentMember?.role ?? null;

  const roleOptions = useMemo(
    () =>
      MEMBER_ROLES.map((role) => ({
        value: role,
        label: translate(`chatbots.members.roles.${role}`),
      })),
    [translate],
  );

  const labels = useMemo((): LiksMembersPanelLabels => {
    return {
      loading: translate('chatbots.members.loading'),
      loadError: translate('chatbots.members.loadError'),
      membersHeading: translate('chatbots.members.membersHeading'),
      membersEmpty: translate('chatbots.members.membersEmpty'),
      othersHeading: translate('chatbots.members.othersHeading'),
      othersEmpty: translate('chatbots.members.othersEmpty'),
      inviteNoResults: translate('chatbots.members.inviteNoResults'),
      add: translate('chatbots.members.add'),
      addMember: translate('chatbots.members.addMember'),
      roleLabel: translate('chatbots.members.roleLabel'),
      inviteRoleLabel: translate('chatbots.members.inviteRoleLabel'),
      removeMember: translate('chatbots.members.removeMember'),
      removeLabel: translate('chatbots.members.removeLabel'),
      removeMemberTitle: translate('chatbots.members.removeMemberTitle'),
      removeMemberMessage: translate('chatbots.members.removeMemberMessage'),
      removeConfirm: translate('chatbots.deleteConfirm'),
      removeCancel: translate('chatbots.deleteCancel'),
      leaveLabel: translate('chatbots.members.leaveLabel'),
      leaveMember: translate('chatbots.members.leaveMember'),
      leaveTitle: translate('chatbots.members.leaveTitle'),
      leaveMessage: translate('chatbots.members.leaveMessage'),
      leaveConfirm: translate('chatbots.members.leaveConfirm'),
      leaveCancel: translate('chatbots.deleteCancel'),
      youBadge: translate('chatbots.members.youBadge'),
      searchLabel: translate('chatbots.members.searchLabel'),
      searchPlaceholder: translate('chatbots.members.searchPlaceholder'),
      searchClear: translate('common.search.clear'),
      unknownUser: translate('chatbots.members.unknownUser'),
      copyEmail: translate('chatbots.members.copyEmail'),
      copyEmailCopied: translate('chatbots.members.copyEmailCopied'),
    };
  }, [translate]);

  const handleInvite = (userId: string, role: ChatbotMemberRole) => {
    setPendingUserId(userId);
    inviteMemberMutation.mutate(
      {
        chatbotId,
        userId,
        role,
      },
      {
        onSuccess: () => {
          toast.success({
            title: translate('chatbots.members.inviteSuccessTitle'),
            description: '',
          });
        },
        onError: (error) => {
          void toastMutationError(
            error,
            translate('chatbots.members.inviteErrorTitle'),
          );
        },
        onSettled: () => {
          setPendingUserId(null);
        },
      },
    );
  };

  const handleRoleChange = (
    panelMember: LiksMembersPanelMember<ChatbotMemberRole>,
    role: ChatbotMemberRole,
  ) => {
    const member = membersByUserId.get(panelMember.userId);
    if (!member || member.role === role) {
      return;
    }

    const username = formatDirectoryUserDisplayName(
      directoryUsersQuery.data?.find(
        (entry) => entry.id === panelMember.userId,
      ),
      labels.unknownUser,
    );

    setPendingUserId(panelMember.userId);
    updateMemberRoleMutation.mutate(
      {
        chatbotId,
        member,
        role,
      },
      {
        onError: (error) => {
          void toastMutationError(
            error,
            translate('chatbots.members.roleErrorTitle', {
              username,
            }),
            translate('chatbots.deleteErrorDescription'),
          );
        },
        onSettled: () => {
          setPendingUserId(null);
        },
      },
    );
  };

  const handleRemove = (
    panelMember: LiksMembersPanelMember<ChatbotMemberRole>,
  ) => {
    if (pendingUserId != null) {
      return;
    }

    setPendingUserId(panelMember.userId);
    removeMemberMutation.mutate(
      {
        chatbotId,
        memberId: panelMember.id,
      },
      {
        onSuccess: () => {
          toast.success({
            title: translate('chatbots.members.removeMemberSuccessTitle'),
            description: '',
          });
        },
        onError: (error) => {
          void toastMutationError(
            error,
            translate('chatbots.deleteErrorTitle'),
            translate('chatbots.deleteErrorDescription'),
          );
        },
        onSettled: () => {
          setPendingUserId(null);
        },
      },
    );
  };

  const handleLeave = () => {
    if (!currentMember?.id) {
      return;
    }

    setIsLeaving(true);
    removeMemberMutation.mutate(
      {
        chatbotId,
        memberId: currentMember.id,
      },
      {
        onSuccess: () => {
          navigate('/chatbots');
        },
        onError: (error) => {
          void toastMutationError(
            error,
            translate('chatbots.members.leaveErrorTitle'),
          );
        },
        onSettled: () => {
          setIsLeaving(false);
        },
      },
    );
  };

  return (
    <LiksMembersPanel
      resourceName={chatbotName}
      members={panelMembers}
      directoryUsers={directoryUsersQuery.data ?? []}
      isLoadingDirectoryUsers={directoryUsersQuery.isLoading}
      directoryUsersError={directoryUsersQuery.isError}
      roleOptions={roleOptions}
      defaultInviteRole="VIEWER"
      currentUserId={user?.id}
      canManageMembers={canManageChatbotMembers(currentMemberRole)}
      canLeave={canLeaveChatbot(currentMemberRole)}
      canManageMember={(member) =>
        canManageChatbotMemberTarget(currentMemberRole, member.userId, user?.id)
      }
      adminRole="ADMIN"
      labels={labels}
      pendingUserId={pendingUserId}
      isLeaving={isLeaving}
      onInvite={handleInvite}
      onRoleChange={handleRoleChange}
      onRemove={handleRemove}
      onLeave={handleLeave}
    />
  );
}
